import React, { useRef } from "react";
import { useNotitieStempel } from "../../../hooks/useNotitieStempel";

const inputStyle: React.CSSProperties = {
  padding: "0.75rem",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius)",
  backgroundColor: "var(--color-surface)",
  color: "var(--color-text)",
  fontSize: "var(--font-body)",
  width: "100%",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  marginBottom: "0.5rem",
  fontWeight: "600",
  display: "block",
};

const helpStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#6b7280",
  marginTop: "0.25rem",
};

export interface ModalTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  required?: boolean;
  id?: string;
  /** Vaste tekst vóór het invoerveld, bijvoorbeeld "+31". */
  prefix?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /**
   * B9: notitiestempel "[Naam dd.mm.jj uu.mm] " bij de eerste focus (alleen
   * bij een textarea, rows > 1); bestaande tekst krijgt het stempel op een
   * nieuwe regel onderaan.
   */
  stempel?: boolean;
}

const prefixStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "0 0.75rem",
  border: "1px solid var(--color-border)",
  borderRight: "none",
  borderRadius: "var(--radius) 0 0 var(--radius)",
  backgroundColor: "var(--color-surface-muted, #f3f4f6)",
  color: "var(--color-text)",
  fontSize: "var(--font-body)",
};

export function ModalTextField({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  rows = 1,
  required,
  id,
  prefix,
  inputMode,
  stempel,
}: ModalTextFieldProps) {
  const fieldId = id || `modal-text-${label.replace(/\s/g, "-")}`;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { onFocus: stempelBijFocus } = useNotitieStempel(textareaRef, {
    actief: !!stempel && rows > 1,
    value,
    onChange,
    maxLength,
  });

  if (rows > 1) {
    return (
      <div className="modal-field">
        <label htmlFor={fieldId} style={labelStyle}>
          {label}
          {required && " *"}
        </label>
        <textarea
          ref={textareaRef}
          id={fieldId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={stempelBijFocus}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          required={required}
          style={{
            ...inputStyle,
            resize: "vertical",
            fontFamily: "inherit",
          }}
        />
        {maxLength !== undefined && (
          <div style={helpStyle}>
            {value.length}/{maxLength} karakters
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="modal-field">
      <label htmlFor={fieldId} style={labelStyle}>
        {label}
        {required && " *"}
      </label>
      {prefix ? (
        <div style={{ display: "flex" }}>
          <span style={prefixStyle}>{prefix}</span>
          <input
            id={fieldId}
            type="text"
            inputMode={inputMode}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            required={required}
            className="form-input"
            style={{
              ...inputStyle,
              borderRadius: "0 var(--radius) var(--radius) 0",
            }}
          />
        </div>
      ) : (
        <input
          id={fieldId}
          type="text"
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          required={required}
          className="form-input"
          style={inputStyle}
        />
      )}
      {maxLength !== undefined && (
        <div style={helpStyle}>
          {value.length}/{maxLength} karakters
        </div>
      )}
    </div>
  );
}
